import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDown, ChevronUp, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { Button } from './button';

const Select = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    onClear?: () => void;
    label?: string;
    selectValue?: string | number;
    size?: 'sm' | 'md';
    error?: string | undefined | null;
    hideLabel?: boolean;
    clear?: boolean;
  }
>(
  (
    {
      className,
      children,
      onClear,
      label,
      selectValue,
      size = 'md',
      clear = true,
      error,
      hideLabel,
      ...props
    },
    ref,
  ) => {
    return (
      <div className='relative flex-1'>
        <SelectPrimitive.Trigger
          value={selectValue}
          ref={ref}
          className={cn(
            'flex h-10 w-full items-center justify-between overflow-hidden rounded-md border border-input bg-transparent px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring  disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
            className,
            `${error && 'border-red'}`,
          )}
          {...props}
        >
          {!hideLabel && (
            <label
              htmlFor={props.id}
              className={cn(
                selectValue
                  ? 'text-[#5F5F5F] font-medium text-xs absolute top-[8px]'
                  : `text-[#FFFFFF] font-medium text-sm absolute  ${size === 'md' ? 'top-[20px]' : 'top-[14px]'}`,
                'transition-all duration-150 cursor-pointer',
              )}
            >
              {label}
            </label>
          )}
          {children}
          {(!selectValue || !clear) && (
            <SelectPrimitive.Icon asChild>
              <ChevronDown className='h-4 w-4 text-white opacity-50 absolute right-[10px]' />
            </SelectPrimitive.Icon>
          )}
        </SelectPrimitive.Trigger>
        {selectValue && clear && (
          <Button
            disabled={props.disabled}
            id={`${props.id}-clear`}
            variant='ghost'
            size='icon'
            className={`absolute right-0  ${hideLabel ? 'bottom-1' : 'bottom-0'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClear?.();
            }}
          >
            <X className='opacity-70 text-white' />
          </Button>
        )}
      </div>
    );
  },
);
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const CustomSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    onClear?: () => void;
    label?: string;
    selectValue?: string;
  }
>(({ className, children, selectValue, ...props }, ref) => (
  <div className='relative'>
    <SelectPrimitive.Trigger
      value={selectValue}
      ref={ref}
      className={cn(
        'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
        className,
      )}
      {...props}
    >
      {/* <div
        className={cn(
          selectValue
            ? 'text-[#5F5F5F] font-medium text-xs absolute top-[8px]'
            : 'text-[#5F5F5F] font-medium text-sm absolute  top-[20px]',
          'transition-all duration-150',
        )}
      >
        {label}
      </div> */}

      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className='h-4 w-4 opacity-50 ' />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
    {/* {selectValue && (
      <Button
        id={`${props.id}-clear`}
        variant='ghost'
        size='icon'
        tabIndex={2}
        className='absolute right-0 bottom-0'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClear?.();
        }}
      >
        <img src='/assets/images/clear.svg' alt='clear' />
      </Button> */}
    {/* )} */}
  </div>
));
CustomSelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp className='h-4 w-4' />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown className='h-4 w-4' />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 max-h-80  overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        position === 'popper' &&
          'data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('py-1.5 pl-8 pr-2 text-sm font-semibold', className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5  px-2 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className='absolute right-2 flex h-3.5 w-3.5 items-center justify-center'>
      <SelectPrimitive.ItemIndicator>
        <CheckIcon className='h-4 w-4' />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-muted', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  CustomSelectTrigger,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
};

interface SelectProps extends Omit<React.HTMLProps<HTMLSelectElement>, 'size'> {
  id: string;
  label?: string;
  values: {
    text?: string;
    value?: string;
    label?: string;
  }[];
  value?: string;
  disabled?: boolean;
  setValue: (value: string) => void;
  size?: 'sm' | 'md';
  error?: string | null | undefined;
  hideLabel?: boolean;
  clear?: boolean;
}

export const SelectComponent: React.FC<SelectProps> = ({
  id,
  label,
  values,
  value = '',
  setValue,
  disabled = false,
  size = 'md',
  error,
  className,
  hideLabel,
  clear,
}) => {
  // clear the selected value
  const clearSelected = () => {
    setValue('');
  };

  // select change handler
  const onSelectChange = (value: string) => {
    setValue(value);
  };

  return (
    <Select onValueChange={onSelectChange} value={value || ''} disabled={disabled}>
      <div className='relative'>
        <SelectTrigger
          onClear={clearSelected}
          id={id}
          data-testid={`${id}-data-testid`}
          className={cn(
            `${size === 'md' ? 'h-[61px]' : 'h-[48px]'} relative p-[10px]`,
            value ? ' items-end  p-[10px]' : '',
            className,
          )}
          label={label}
          selectValue={value}
          disabled={disabled}
          size={size}
          error={error}
          hideLabel={!!hideLabel}
          clear={clear}
        >
          <p className={`whitespace-nowrap ${hideLabel ? 'pb-1' : ''}`}>
            <SelectValue />
          </p>
        </SelectTrigger>
        {error ? (
          <p
            aria-label='error'
            aria-errormessage={error}
            className='text-red text-xs absolute top-[63px]'
          >
            {error}
          </p>
        ) : null}
      </div>
      <SelectContent className='pointer-events-auto'>
        {values?.map((item) => (
          <SelectItem
            key={item?.value}
            value={item?.value ? item?.value : ''}
            className='cursor-pointer'
          >
            {item?.text || item?.label || item?.value}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
